import { useEffect, useRef } from 'react';

const highlights = [
  {
    icon: 'ri-trophy-line',
    iconBg: 'bg-amber-50',
    iconColor: 'text-amber-700',
    stat: 'Top 1%',
    label: 'of accommodations worldwide — TripAdvisor Travelers\' Choice Award',
  },
  {
    icon: 'ri-star-fill',
    iconBg: 'bg-gold/10',
    iconColor: 'text-gold',
    stat: '5-Star',
    label: 'TripAdvisor rating earned through years of genuine hospitality',
  },
  {
    icon: 'ri-shield-check-line',
    iconBg: 'bg-forest-50',
    iconColor: 'text-forest',
    stat: 'Stable',
    label: 'Revenues cover operating expenses — systems work, foundation intact',
  },
];

export default function HonestCircumstancesSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('is-visible'); }),
      { threshold: 0.08 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    itemRefs.current.forEach(el => { if (el) observer.observe(el); });
    return () => observer.disconnect();
  }, []);

  return (
    <section id="honest-circumstances" ref={sectionRef} className="py-24 md:py-32 bg-cream">
      <div className="max-w-4xl mx-auto px-6 md:px-12">

        {/* Badge + Title */}
        <div className="text-center mb-14 reveal">
          <div className="inline-flex items-center gap-2 bg-gold/10 text-gold rounded-full px-4 py-1.5 mb-6">
            <i className="ri-quill-pen-line text-sm w-4 h-4 flex items-center justify-center" />
            <span className="font-sans text-xs tracking-widest uppercase">A Seller's Note</span>
          </div>
          <h2 className="font-serif text-3xl md:text-5xl text-forest-dark leading-tight">
            A Rare Opportunity Born From<br /><em>Honest Circumstances</em>
          </h2>
        </div>

        {/* Opening pull quote */}
        <div
          ref={el => { itemRefs.current[0] = el; }}
          className="reveal border-l-4 border-gold pl-7 mb-12"
        >
          <p className="font-serif text-xl md:text-2xl text-forest-dark leading-relaxed italic">
            "Montebrisa didn't end up on the market because something went wrong. It ended up here because something went right — for a long time — and the people who built it are ready to pass it to someone with the energy and vision to take it further."
          </p>
        </div>

        {/* Body paragraphs */}
        <div
          ref={el => { itemRefs.current[1] = el; }}
          className="reveal space-y-6 mb-14"
        >
          <p className="font-sans text-base text-gray-600 leading-relaxed">
            The COVID-19 pandemic reshaped the global hospitality industry in ways that are still being felt across Central America. Matagalpa was no exception. The recovery has been gradual, and for absentee owners managing a property from a distance, the path forward requires a level of hands-on attention that the current owners are no longer positioned to give.
          </p>
          <p className="font-sans text-base text-gray-600 leading-relaxed">
            They built something extraordinary. An award-winning boutique property ranked in the top 1% of accommodations worldwide. A 5-star TripAdvisor rating earned through years of genuine hospitality. A beautifully restored 1940s Art Déco villa with infrastructure that most buyers spend years and significant capital trying to create from scratch.
          </p>
        </div>

        {/* Highlight stats row */}
        <div
          ref={el => { itemRefs.current[2] = el; }}
          className="reveal grid grid-cols-1 sm:grid-cols-3 gap-4 mb-14"
        >
          {highlights.map((h, idx) => (
            <div
              key={h.stat}
              ref={el => { itemRefs.current[3 + idx] = el; }}
              className="reveal bg-white rounded-xl p-5 border border-cream-dark text-center"
              style={{ transitionDelay: `${idx * 0.1}s` }}
            >
              <div className={`w-11 h-11 flex items-center justify-center ${h.iconBg} rounded-xl mx-auto mb-3`}>
                <i className={`${h.icon} ${h.iconColor} text-lg`} />
              </div>
              <p className="font-serif text-2xl text-forest-dark font-medium mb-1">{h.stat}</p>
              <p className="font-sans text-xs text-gray-500 leading-relaxed">{h.label}</p>
            </div>
          ))}
        </div>

        {/* Continuation paragraph */}
        <div
          ref={el => { itemRefs.current[6] = el; }}
          className="reveal mb-14"
        >
          <p className="font-sans text-base text-gray-600 leading-relaxed">
            Today, revenues cover operating expenses — which means the business is stable, the systems work, and the foundation is completely intact. What it needs now is what the current owners honestly acknowledge they can no longer provide — a committed owner-operator or investor with fresh energy and a vision for what Montebrisa can become in the hands of someone fully devoted to its growth.
          </p>
        </div>

        {/* Gold divider */}
        <div className="flex items-center gap-4 mb-14 reveal">
          <div className="h-px flex-1 bg-gold/25" />
          <div className="w-1.5 h-1.5 rounded-full bg-gold" />
          <div className="h-px flex-1 bg-gold/25" />
        </div>

        {/* Closing statement */}
        <div
          ref={el => { itemRefs.current[7] = el; }}
          className="reveal bg-white rounded-2xl border border-cream-dark p-8 md:p-10 text-center"
        >
          <p className="font-sans text-base text-gray-600 leading-relaxed mb-5">
            The platform is proven. The reputation is established. The hard work of building has already been done.
          </p>
          <p className="font-serif text-xl md:text-2xl text-forest-dark leading-snug">
            What happens next is up to the <em>right buyer.</em>
          </p>
          <div className="mt-7">
            <button
              onClick={() => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })}
              className="inline-flex items-center gap-2 px-7 py-3 bg-forest text-white font-sans text-xs tracking-widest uppercase rounded-md hover:bg-forest-dark transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-mail-send-line w-4 h-4 flex items-center justify-center" />
              Express Your Interest
            </button>
          </div>
        </div>

      </div>
    </section>
  );
}
